import {
  CHECK_LIST,
  HIGHLIGHT,
  LINK,
  Transformer,
  TRANSFORMERS as DEFAULT_TRANSFORMERS,
} from '@lexical/markdown';
import { $createTextNode, ElementNode, LexicalNode, TextNode } from 'lexical';
import { isEmpty } from 'lodash';

import { diagnostics } from '@/utils/diagnostics';

import { linkTransformer } from './linkTransformer';
import { unescapeInnerMarkdown } from './markdownEscaping';
import {
  $createCitationNode,
  CitationNode,
} from './plugins/CitationPlugin/CitationNode';
import {
  CITATION_TOKEN_REGEX,
  formatCitationToken,
} from './plugins/CitationPlugin/citationTokens';
import { $detectBoundaryFormatLeak } from './plugins/FontSizePlugin/boundaryFormatLeak';
import {
  $createFontSizeNode,
  $isFontSizeNode,
  FontSizeNode,
} from './plugins/FontSizePlugin/FontSizeNode';
import { highlightTransformer } from './plugins/HighlightPlugin/highlightTransformer';
import { internalLinkTransformer } from './plugins/InternalLinkPlugin/InternalLinkNode';
import { createTableTransformer } from './plugins/MarkdownTableTransformer';
import {
  $createMermaidNode,
  MermaidNode,
} from './plugins/MermaidPlugin/MermaidNode';
import {
  literalPlusEscapeTransformer,
  underlineTransformer,
} from './underlineTransformer';

const mermaidTransformer: Transformer = {
  dependencies: [MermaidNode],
  export: (node: LexicalNode) => {
    if (node instanceof MermaidNode) {
      return `\`\`\`mermaid\n${node.getCode()}\n\`\`\``;
    }
    return null;
  },
  regExpStart: /^```mermaid\s*$/,
  regExpEnd: /^```\s*$/,
  replace: (
    rootNode: ElementNode,
    _children: Array<LexicalNode> | null,
    _startMatch: Array<string>,
    _endMatch: Array<string> | null,
    linesInBetween: Array<string> | null
  ) => {
    const code = linesInBetween?.join('\n').trim() ?? '';
    if (!code) {
      return;
    }
    const mermaidNode = $createMermaidNode(code);
    rootNode.append(mermaidNode);
  },
  type: 'multiline-element',
};

const citationTransformer: Transformer = {
  dependencies: [CitationNode],
  export: (
    node: LexicalNode,
    _exportChildren: (node: ElementNode) => string,
    _exportFormat: (node: TextNode, textContent: string) => string
  ) => {
    if (node instanceof CitationNode) {
      return formatCitationToken(node.getParagraphIds());
    }
    return null;
  },
  importRegExp: CITATION_TOKEN_REGEX,
  regExp: CITATION_TOKEN_REGEX,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, citationsContent] = match;

    // this can happen with normal brackets
    if (!citationsContent) return;

    let paragraphIds: string[] = [];
    try {
      paragraphIds = JSON.parse(`[${citationsContent}]`);
    } catch (error) {
      // notify here, and remove the text node below
      diagnostics.error('Error parsing citation content:', error as Error);
    }

    if (!Array.isArray(paragraphIds) || isEmpty(paragraphIds)) {
      textNode.remove();
      return;
    }

    const citationNode = $createCitationNode(paragraphIds);
    textNode.replace(citationNode);
  },
  trigger: '[',
  type: 'text-match',
};

// Matches [size=N]text[/size] anywhere in a line (used during markdown import)
const FONT_SIZE_IMPORT_REGEXP = /\[size=(\d+)\](.+?)\[\/size\]/;

// Matches [size=N]text[/size] at end of line (used during live typing)
const FONT_SIZE_LIVE_REGEXP = /\[size=(\d+)\](.+?)\[\/size\]$/;

const fontSizeTransformer: Transformer = {
  dependencies: [FontSizeNode],
  export: (
    node: LexicalNode,
    exportChildren: (node: ElementNode) => string
  ) => {
    if (!$isFontSizeNode(node)) return null;
    const childrenMarkdown = exportChildren(node);
    // When a text-format span (bold, italic, etc.) crosses the FontSize
    // boundary, Lexical's exportTextFormat skip-closing logic produces
    // unbalanced `**`/`*`/etc. markers that fail to round-trip on import.
    // Inject balanced markers around the inner content. See
    // boundaryFormatLeak.ts for the full rationale.
    const { outerPrefix, innerOpen, innerClose, outerSuffix } =
      $detectBoundaryFormatLeak(node);
    return `${outerPrefix}[size=${node.getFontSize()}]${innerOpen}${childrenMarkdown}${innerClose}[/size]${outerSuffix}`;
  },
  importRegExp: FONT_SIZE_IMPORT_REGEXP,
  regExp: FONT_SIZE_LIVE_REGEXP,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, sizeStr, text] = match;
    if (!sizeStr || !text) return;

    const fontSize = parseInt(sizeStr, 10);
    const fontSizeNode = $createFontSizeNode(fontSize);
    // Unescape markdown characters so that inner formatting (e.g. **bold**)
    // is recognised by text-format transformers during recursive import.
    const textChild = $createTextNode(unescapeInnerMarkdown(text));
    textChild.setFormat(textNode.getFormat());
    fontSizeNode.append(textChild);
    textNode.replace(fontSizeNode);

    // Return the child so Lexical recursively applies format transformers
    // (bold, italic, etc.) to the inner content.
    return textChild;
  },
  trigger: ']',
  type: 'text-match',
};

// Base transformers without TABLE to avoid circular dependency in TABLE's internal processing.
// The built-in HIGHLIGHT transformer (plain `==text==`) is replaced by our
// color-aware highlightTransformer (`==color:text==`), and the built-in LINK
// transformer by our linkTransformer, which accepts balanced parentheses in
// URLs.
const baseTransformers: Transformer[] = [
  // First, so that it exports every text node that contains a literal `++`.
  literalPlusEscapeTransformer,
  // Ahead of the LINK transformers so it out-matches them for luminary://
  // targets; otherwise those links become dead about:blank anchors. Registered
  // here (not via ChatMarkdown props) so the chat agent's internal links work
  // in any editor that renders chat markdown, independent of how that surface
  // wires up the editor.
  internalLinkTransformer,
  CHECK_LIST,
  mermaidTransformer,
  highlightTransformer,
  fontSizeTransformer,
  linkTransformer,
  ...DEFAULT_TRANSFORMERS.filter((t) => t !== HIGHLIGHT && t !== LINK),
  underlineTransformer,
  citationTransformer,
];

// Create TABLE transformer with base transformers, then add it to the full list
// This breaks the circular dependency while allowing tables to contain formatted content
export const markdownTransformers: Transformer[] = [
  ...baseTransformers,
  createTableTransformer(baseTransformers),
];
